import { Identifier } from "@/types/domain";
import { Locale } from "@/types/locale";
import { localizeFrom } from "@lib/i18n/utils";
import { doesProposalVoteNeedReview } from "@features/events-program/lib/proposal-revote";
import {
  ActivityMemberPreview,
  ProposalAssignmentAggregateEntity,
} from "@features/events-program/types/proposal-assignment-aggregate";
import { OPEN_VOTING_WORKFLOW_STATUSES } from "@features/events-program/types/proposal-vote";
import { VotingPanelVoteVisibilityState } from "@features/events-program/ui/votes";
import { ProposalClosedStatuses } from "@features/proposals/proposals-consts";
import {
  ProposalAggWorkflowOutputView,
  ProposalWorkflowStatus,
} from "@features/proposals/types/proposal";
import routes from "@features/routes";

export type AssignmentNotificationCategory = "awaitingVote" | "curatorship" | "viewed";
export type AssignmentNotificationPanelTab = "all" | AssignmentNotificationCategory;

export type AssignmentNotificationQuickAction = {
  activityId: Identifier;
  proposalId: Identifier;
  tab: "activity" | "vote";
};

export type AssignmentNotificationItem = {
  activityId?: Identifier;
  assignmentKind?: string;
  assignmentKinds: string[];
  assignmentStatus?: string;
  assigneeId?: Identifier;
  categories: AssignmentNotificationCategory[];
  category: AssignmentNotificationCategory;
  eventId?: Identifier;
  eventTitle: string;
  fullListHref?: string;
  id: Identifier;
  proposalId?: Identifier;
  proposalTitle: string;
  quickAction?: AssignmentNotificationQuickAction;
  ratingHidden: boolean;
  ratingLine: string;
  requiresAction: boolean;
  speakerLine: string;
  topics: string[];
  voteCountLine: string;
  workflow?: ProposalAggWorkflowOutputView;
};

export type AssignmentNotificationsCounters = {
  all: number;
  awaitingVote: number;
  curatorship: number;
  requiresAction: number;
  viewed: number;
};

export type AssignmentNotificationsModel = {
  counters: AssignmentNotificationsCounters;
  items: AssignmentNotificationItem[];
};

export type AssignmentNotificationVoteVisibilityStateResolver = (
  aggregate: ProposalAssignmentAggregateEntity,
) => VotingPanelVoteVisibilityState | undefined;

type BuildAssignmentNotificationsModelOptions = {
  readonly getVoteVisibilityState?: AssignmentNotificationVoteVisibilityStateResolver;
};

const closedProposalWorkflowStatuses = new Set<ProposalWorkflowStatus>(
  ProposalClosedStatuses.filter((status): status is ProposalWorkflowStatus => status !== undefined),
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getContentData = (value?: { content?: { data?: unknown } }) =>
  isRecord(value?.content?.data) ? value.content.data : undefined;

const getLocalizedField = (
  data: Record<string, unknown> | undefined,
  field: string,
  locale: Locale,
) => localizeFrom(data?.[field] as never, locale);

const getInvariantArrayField = (
  data: Record<string, unknown> | undefined,
  field: string,
): string[] => {
  const value = data?.[field];

  if (!isRecord(value) || !Array.isArray(value.iv)) {
    return [];
  }

  return Array.from(
    new Set(value.iv.map((item) => (typeof item === "string" ? item.trim() : "")).filter(Boolean)),
  );
};

const getFiniteNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const getNumberLine = (value: unknown, locale: Locale) => {
  const numberValue = getFiniteNumber(value);

  return numberValue === undefined
    ? ""
    : new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(numberValue);
};

const getIntegerLine = (value: unknown) => {
  const numberValue = getFiniteNumber(value);

  return numberValue === undefined ? "" : numberValue.toString();
};

const getNonEmptyUniqueValues = <T extends string | number>(values: (T | undefined)[]) =>
  Array.from(new Set(values.filter((value): value is T => value !== undefined)));

const getMemberName = (member: ActivityMemberPreview, locale: Locale) => {
  const data = getContentData(member.eventProfile?.biography);
  const firstName = getLocalizedField(data, "firstName", locale).trim();
  const lastName = getLocalizedField(data, "lastName", locale).trim();

  return [firstName, lastName].filter(Boolean).join(" ");
};

const getSpeakerLine = (members: ActivityMemberPreview[] | undefined, locale: Locale) =>
  (members ?? [])
    .filter((member) => !member.role || member.role === "SPEAKER")
    .map((member) => getMemberName(member, locale))
    .filter(Boolean)
    .join(", ");

const isVotingWorkflowOpen = (aggregate: ProposalAssignmentAggregateEntity) => {
  const status = aggregate.proposal?.workflow?.status;

  return status !== undefined && OPEN_VOTING_WORKFLOW_STATUSES.has(status);
};

const isClosedAssignmentProposal = (aggregate: ProposalAssignmentAggregateEntity) => {
  const status = aggregate.proposal?.workflow?.status;

  return typeof status === "string" && closedProposalWorkflowStatuses.has(status);
};

const isOwnVotePendingReview = (aggregate: ProposalAssignmentAggregateEntity) =>
  doesProposalVoteNeedReview(aggregate.proposal?.vote, aggregate.proposal?.workflow);

const isActiveReviewerWithoutVote = (aggregate: ProposalAssignmentAggregateEntity) =>
  aggregate.assignment?.status === "ACTIVE" &&
  aggregate.assignment.kind === "REVIEWER" &&
  (!aggregate.proposal?.vote || isOwnVotePendingReview(aggregate)) &&
  isVotingWorkflowOpen(aggregate);

const isActiveCurator = (aggregate: ProposalAssignmentAggregateEntity) =>
  aggregate.assignment?.status === "ACTIVE" && aggregate.assignment.kind === "CURATOR";

const isActiveCuratorWithoutVoteWithVoteRight = (aggregate: ProposalAssignmentAggregateEntity) =>
  isActiveCurator(aggregate) &&
  (!aggregate.proposal?.vote || isOwnVotePendingReview(aggregate)) &&
  aggregate.proposal?.rights?.canCreateProposalVote === true &&
  isVotingWorkflowOpen(aggregate);

const getCategories = (
  aggregate: ProposalAssignmentAggregateEntity,
): AssignmentNotificationCategory[] => {
  if (isActiveCuratorWithoutVoteWithVoteRight(aggregate)) {
    return ["awaitingVote", "curatorship"];
  }

  if (isActiveReviewerWithoutVote(aggregate)) {
    return ["awaitingVote"];
  }

  if (isActiveCurator(aggregate)) {
    return ["curatorship"];
  }

  return ["viewed"];
};

const getQuickAction = (
  aggregate: ProposalAssignmentAggregateEntity,
): AssignmentNotificationQuickAction | undefined => {
  const proposalId = aggregate.proposal?.id;
  const activityId = aggregate.proposal?.activity?.id;

  if (proposalId === undefined || activityId === undefined) {
    return undefined;
  }

  if (
    isActiveReviewerWithoutVote(aggregate) ||
    isActiveCuratorWithoutVoteWithVoteRight(aggregate)
  ) {
    return { proposalId, activityId, tab: "vote" };
  }

  if (isActiveCurator(aggregate)) {
    return { proposalId, activityId, tab: "activity" };
  }

  return undefined;
};

const getFullListHref = (aggregate: ProposalAssignmentAggregateEntity) => {
  const eventId = aggregate.event?.id;
  const idOrSlug = aggregate.event?.workspaceId;

  if (eventId === undefined || !idOrSlug) {
    return undefined;
  }

  return routes.events_program.pages.event_voting_panel.path({ idOrSlug, eventId });
};

const getRatingLine = (
  aggregate: ProposalAssignmentAggregateEntity,
  locale: Locale,
  getVoteVisibilityState?: AssignmentNotificationVoteVisibilityStateResolver,
) => {
  const ratingLine = getNumberLine(aggregate.proposal?.rating, locale);

  if (!ratingLine) {
    return { ratingHidden: false, ratingLine };
  }

  const hasOwnVote = aggregate.proposal?.vote !== undefined;
  const programCommitteeShowAll =
    getVoteVisibilityState?.(aggregate)?.programCommitteeShowAll === true;
  const ratingHidden =
    (!hasOwnVote || isOwnVotePendingReview(aggregate)) && !programCommitteeShowAll;

  return {
    ratingHidden,
    ratingLine: ratingHidden ? "" : ratingLine,
  };
};

const toAssignmentNotificationItem = (
  aggregate: ProposalAssignmentAggregateEntity,
  locale: Locale,
  index: number,
  options?: BuildAssignmentNotificationsModelOptions,
): AssignmentNotificationItem => {
  const categories = getCategories(aggregate);
  const category = getPrimaryCategory(categories);
  const requiresAction = categories.includes("awaitingVote") || categories.includes("curatorship");
  const eventData = getContentData(aggregate.event);
  const activityData = getContentData(aggregate.proposal?.activity);
  const proposalId = aggregate.proposal?.id ?? aggregate.assignment?.proposalId;
  const activityId = aggregate.proposal?.activity?.id;
  const eventId =
    aggregate.event?.id ?? aggregate.proposal?.eventId ?? aggregate.proposal?.activity?.eventId;
  const assignmentKind = aggregate.assignment?.kind;
  const { ratingHidden, ratingLine } = getRatingLine(
    aggregate,
    locale,
    options?.getVoteVisibilityState,
  );

  return {
    id: aggregate.id ?? aggregate.assignment?.id ?? aggregate.proposal?.id ?? `assignment-${index}`,
    proposalId,
    activityId,
    eventId,
    assigneeId: aggregate.assignment?.assigneeId,
    assignmentKind,
    assignmentKinds: assignmentKind ? [assignmentKind] : [],
    assignmentStatus: aggregate.assignment?.status,
    category,
    categories,
    requiresAction,
    eventTitle: getLocalizedField(eventData, "title", locale),
    proposalTitle: getLocalizedField(activityData, "title", locale),
    speakerLine: getSpeakerLine(aggregate.proposal?.members, locale),
    topics: getInvariantArrayField(activityData, "topics"),
    ratingHidden,
    ratingLine,
    voteCountLine: getIntegerLine(aggregate.proposal?.voteCount),
    fullListHref: getFullListHref(aggregate),
    quickAction: getQuickAction(aggregate),
    workflow: aggregate.proposal?.workflow as ProposalAggWorkflowOutputView | undefined,
  };
};

const createEmptyCounters = (): AssignmentNotificationsCounters => ({
  all: 0,
  awaitingVote: 0,
  curatorship: 0,
  viewed: 0,
  requiresAction: 0,
});

const categoryPriority: Record<AssignmentNotificationCategory, number> = {
  awaitingVote: 0,
  curatorship: 1,
  viewed: 2,
};

const getPrimaryCategory = (
  categories: AssignmentNotificationCategory[],
): AssignmentNotificationCategory =>
  [...categories].sort((left, right) => categoryPriority[left] - categoryPriority[right])[0] ??
  "viewed";

const quickActionPriority: Record<AssignmentNotificationQuickAction["tab"], number> = {
  vote: 0,
  activity: 1,
};

const getPreferredQuickAction = (
  items: AssignmentNotificationItem[],
): AssignmentNotificationQuickAction | undefined =>
  items
    .map((item) => item.quickAction)
    .filter(
      (quickAction): quickAction is AssignmentNotificationQuickAction => quickAction !== undefined,
    )
    .sort((left, right) => quickActionPriority[left.tab] - quickActionPriority[right.tab])[0];

const getAllTabGroupKey = (item: AssignmentNotificationItem) => {
  if (item.proposalId !== undefined) {
    return `proposal:${item.proposalId}`;
  }

  if (item.activityId !== undefined) {
    return `activity:${item.activityId}`;
  }

  return `assignment:${item.id}`;
};

export const groupAssignmentNotificationItemsForAllTab = (
  items: AssignmentNotificationItem[],
): AssignmentNotificationItem[] => {
  const groups = new Map<string, AssignmentNotificationItem[]>();

  for (const item of items) {
    const key = getAllTabGroupKey(item);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }

  return Array.from(groups.values()).flatMap((group) => {
    const first = group[0];

    if (!first) {
      return [];
    }

    const categories = getNonEmptyUniqueValues(group.flatMap((item) => item.categories));
    const assignmentKinds = getNonEmptyUniqueValues(group.flatMap((item) => item.assignmentKinds));
    const category = getPrimaryCategory(categories);
    const ratingHidden = group.some((item) => item.ratingHidden);
    const ratingLine = ratingHidden
      ? ""
      : (group.find((item) => item.ratingLine)?.ratingLine ?? "");

    return [
      {
        ...first,
        assignmentKind: assignmentKinds[0],
        assignmentKinds,
        categories,
        category,
        quickAction: getPreferredQuickAction(group),
        ratingHidden,
        ratingLine,
        requiresAction: group.some((item) => item.requiresAction),
      },
    ];
  });
};

export const buildAssignmentNotificationsModel = (
  aggregates: ProposalAssignmentAggregateEntity[] | undefined,
  locale: Locale,
  options?: BuildAssignmentNotificationsModelOptions,
): AssignmentNotificationsModel => {
  const items = (aggregates ?? [])
    .filter((aggregate) => !isClosedAssignmentProposal(aggregate))
    .map((aggregate, index) => toAssignmentNotificationItem(aggregate, locale, index, options));
  const counters = items.reduce(createEmptyCounters, createEmptyCounters());

  for (const item of items) {
    for (const category of item.categories) {
      counters[category] += 1;
    }

    if (item.requiresAction) {
      counters.requiresAction += 1;
    }
  }
  counters.all = groupAssignmentNotificationItemsForAllTab(items).length;

  return { items, counters };
};

export const filterAssignmentNotificationItems = (
  items: AssignmentNotificationItem[],
  tab: AssignmentNotificationPanelTab,
) =>
  tab === "all"
    ? groupAssignmentNotificationItemsForAllTab(items)
    : items.filter((item) => item.categories.includes(tab));
